import { useCallback, useState } from "react";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import type { IntakeData } from "@/lib/types";
import { CardActions } from "./CardPrimitives";
import { useIntakeMobileChromeRegistration } from "../IntakeMobileChromeContext";
import { Checkbox } from "@/components/ui/checkbox";
import {
  getOtherSpecialtyReviewRows,
  sendOtherSpecialtyInquiryMaileroo,
} from "../otherSpecialtyMail";

interface OtherSpecialtyReviewCardProps {
  data: IntakeData;
  onBack: () => void;
  onSent: () => void;
}

export function OtherSpecialtyReviewCard({
  data,
  onBack,
  onSent,
}: OtherSpecialtyReviewCardProps) {
  const { _ } = useLingui();
  const [confirmed, setConfirmed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const rows = getOtherSpecialtyReviewRows(data);

  const handleSend = useCallback(async () => {
    if (!confirmed || sending) return;
    setSendError(null);
    setSending(true);
    try {
      const result = await sendOtherSpecialtyInquiryMaileroo(data);
      if (!result.ok) {
        setSendError(result.error ?? _(msg`Could not send email. Please try again.`));
        return;
      }
      onSent();
    } catch (e) {
      setSendError(
        e instanceof Error ? e.message : _(msg`Could not send email. Please try again.`),
      );
    } finally {
      setSending(false);
    }
  }, [confirmed, sending, data, onSent, _]);

  useIntakeMobileChromeRegistration(
    () => ({
      onContinue: handleSend,
      onCancel: onBack,
      continueLabel: _(msg`Send inquiry`),
      continueLoading: sending,
      continueDisabled: !confirmed,
    }),
    [handleSend, onBack, _, sending, confirmed],
  );

  return (
    <div className="w-full max-w-2xl">
      <div className="mb-8">
        <h2 className="text-[clamp(22px,5vw,34px)] font-bold leading-tight text-gray-900">
          <Trans>Review your inquiry</Trans>
        </h2>
        <p className="mt-2 text-sm text-gray-500">
          <Trans>
            Other specialty requests are sent to our team securely (not through
            the standard intake API). Please confirm everything looks correct.
          </Trans>
        </p>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="border-b border-gray-100 bg-gray-50 px-4 py-3 sm:px-5">
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">
            <Trans>Your details</Trans>
          </h3>
        </div>
        <div className="divide-y divide-gray-50">
          {rows.map((row) => (
            <div
              key={row.label}
              className="flex flex-col items-start gap-1 px-4 py-3 sm:flex-row sm:gap-3 sm:px-5"
            >
              <span className="pt-0.5 text-xs text-gray-400 sm:w-44 sm:flex-shrink-0">
                {row.label}
              </span>
              <span className="flex-1 break-words text-sm font-medium text-gray-800">
                {row.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div
        id="other-specialty-confirm"
        className="mt-6 rounded-xl border border-[#166B93]/15 bg-[#166B93]/5 p-4"
      >
        <div className="flex items-start gap-3">
          <Checkbox
            id="other-specialty-confirm-checkbox"
            checked={confirmed}
            onCheckedChange={(c) => setConfirmed(c === true)}
            className="mt-1"
          />
          <label
            htmlFor="other-specialty-confirm-checkbox"
            className="cursor-pointer text-sm font-medium text-gray-900"
          >
            <Trans>
              I confirm this information is accurate and I agree to be contacted
              about this inquiry by email.
            </Trans>
          </label>
        </div>
      </div>

      {!confirmed && (
        <p className="mt-2 text-center text-xs text-gray-500">
          <Trans>Please confirm to send your inquiry</Trans>
        </p>
      )}

      {sendError && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-700">{sendError}</p>
        </div>
      )}

      <div className="mt-8">
        <CardActions
          onNext={handleSend}
          onBack={onBack}
          nextLabel={_(msg`Send inquiry`)}
          nextLoading={sending}
          nextDisabled={!confirmed}
        />
      </div>
    </div>
  );
}
